/**
 * API client with automatic auth header injection and error normalization.
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Request interceptor: inject auth token ───────────────────
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('sf_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor: normalize errors ───────────────────
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Try refresh
      const refreshToken = localStorage.getItem('sf_refresh_token');
      if (refreshToken && error.config && !error.config.url?.includes('/auth/refresh')) {
        try {
          const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
          const newToken = data.data.token;
          localStorage.setItem('sf_token', newToken);
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return api.request(error.config);
        } catch (refreshErr) {
          // Refresh failed — clear auth and redirect
          localStorage.removeItem('sf_token');
          localStorage.removeItem('sf_refresh_token');
          window.location.href = '/auth';
          return Promise.reject(refreshErr);
        }
      }
    }
    return Promise.reject(error);
  }
);

// ── AI-specific client with 90s timeout ──────────────────────
export const aiApi: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 90000,
  headers: { 'Content-Type': 'application/json' },
});

// Share the same interceptors
aiApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('sf_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

aiApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('sf_refresh_token');
      if (refreshToken && error.config && !error.config.url?.includes('/auth/refresh')) {
        try {
          const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
          const newToken = data.data.token;
          localStorage.setItem('sf_token', newToken);
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return aiApi.request(error.config);
        } catch (refreshErr) {
          localStorage.removeItem('sf_token');
          localStorage.removeItem('sf_refresh_token');
          window.location.href = '/auth';
          return Promise.reject(refreshErr);
        }
      }
    }
    return Promise.reject(error);
  }
);

// ── Typed API helpers ─────────────────────────────────────────
export interface ApiError {
  code: string;
  message: string;
  fields?: Record<string, string>;
}

export function extractApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    const raw = error.response.data.error;
    // Guard against non-object error payloads (some endpoints return error as a string)
    if (typeof raw === 'string') {
      return { code: 'API_ERROR', message: raw };
    }
    // Ensure message is always a string (prevents React error #31)
    return {
      ...raw,
      message: typeof raw.message === 'string' ? raw.message : (raw.message ? JSON.stringify(raw.message) : 'An unexpected error occurred'),
    } as ApiError;
  }
  return { code: 'UNKNOWN_ERROR', message: 'An unexpected error occurred' };
}

export function isOnline(): boolean {
  return navigator.onLine;
}
